import type { Card } from "@/context/AppContext";

export type RarityBucket =
  | "common"
  | "uncommon"
  | "rare"
  | "holo"
  | "ultraRare";

export const RARITY_BUCKETS: RarityBucket[] = [
  "common",
  "uncommon",
  "rare",
  "holo",
  "ultraRare",
];

const BUCKET_PRIORITY: Record<RarityBucket, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  holo: 3,
  ultraRare: 4,
};

const BUCKET_LABEL: Record<RarityBucket, string> = {
  common: "common",
  uncommon: "uncommon",
  rare: "rare",
  holo: "holo",
  ultraRare: "ultra rare",
};

export type WeightProfileName =
  | "standard"
  | "premium"
  | "bulk"
  | "pokemonType"
  | "trainer"
  | "energy";

export interface WeightProfile {
  name: WeightProfileName;
  bundleSize: number;
  weights: Record<RarityBucket, number>;
  minGuarantees: Partial<Record<RarityBucket, number>>;
  maxDuplicatesPerCard: number;
}

export interface BucketShortfall {
  bucket: RarityBucket;
  target: number;
  filled: number;
  message: string;
}

export type PickReasonKind = "minGuarantee" | "weight" | "backfill";

export interface PickReason {
  /** Rarity bucket the card was placed into. */
  bucket: RarityBucket;
  /**
   * Why the card was selected:
   *  - `minGuarantee` — bumped in to satisfy the bucket's min-guarantee floor.
   *  - `weight` — pulled in to fill the bucket's weight-driven target.
   *  - `backfill` — added in pass 2 to top the bundle up to its size.
   */
  kind: PickReasonKind;
  /** Short, user-facing label suitable for a badge. */
  label: string;
}

export interface WeightedPickResult {
  cards: Card[];
  selectedQty: number;
  targetSize: number;
  bucketTargets: Record<RarityBucket, number>;
  bucketFilled: Record<RarityBucket, number>;
  bucketShortfalls: BucketShortfall[];
  totalShortfall: number;
  /** Per-card explanation of why each picked row was chosen. Keyed by `card.id`. */
  reasons: Record<string, PickReason>;
}

/**
 * After the two-axis refactor, the print-treatment dimension lives on
 * `Card.finish`, not the rarity enum. Premium / Holo / Reverse Holo cards
 * are now `rarity: "Rare"` with the appropriate finish; check finish first
 * so the picker still routes them to the ultraRare / holo buckets.
 */
export function normalizeRarityBucket(
  rarity: string,
  finish?: string,
): RarityBucket {
  if (finish === "Premium") return "ultraRare";
  if (finish === "Holo" || finish === "Reverse Holo") return "holo";
  if (rarity === "Rare") return "rare";
  if (rarity === "Uncommon") return "uncommon";
  return "common";
}

export function applyProfileFilter(
  profile: WeightProfileName,
  cards: Card[],
): Card[] {
  if (profile === "trainer") {
    return cards.filter((c) => c.tcgSupertype?.toLowerCase() === "trainer");
  }
  if (profile === "energy") {
    return cards.filter((c) => c.tcgSupertype?.toLowerCase() === "energy");
  }
  if (profile === "pokemonType") {
    return cards.filter((c) => {
      const sup = c.tcgSupertype?.toLowerCase();
      const isPokemon = sup === "pokémon" || sup === "pokemon";
      return isPokemon && (c.tcgTypes?.length ?? 0) > 0;
    });
  }
  return cards;
}

function emptyBucketRecord(): Record<RarityBucket, number> {
  return { common: 0, uncommon: 0, rare: 0, holo: 0, ultraRare: 0 };
}

/**
 * Distribute `bundleSize` across buckets according to weights, then bump
 * buckets up to their min guarantees by decrementing other buckets so the
 * totals still sum to `bundleSize`. If the minimums together exceed
 * `bundleSize`, clamp them proportionally.
 */
export function getTargetCounts(
  profile: WeightProfile,
): Record<RarityBucket, number> {
  const { bundleSize, weights, minGuarantees } = profile;

  const minSum = RARITY_BUCKETS.reduce(
    (s, b) => s + (minGuarantees[b] ?? 0),
    0,
  );

  if (minSum > bundleSize && minSum > 0) {
    // Mins exceed bundle. Clamp proportionally so totals still sum to bundleSize.
    const clamped = emptyBucketRecord();
    let allocated = 0;
    const fractions: { b: RarityBucket; frac: number }[] = [];
    for (const b of RARITY_BUCKETS) {
      const raw = ((minGuarantees[b] ?? 0) / minSum) * bundleSize;
      clamped[b] = Math.floor(raw);
      allocated += clamped[b];
      fractions.push({ b, frac: raw - Math.floor(raw) });
    }
    fractions.sort((a, x) => x.frac - a.frac);
    let i = 0;
    while (allocated < bundleSize && i < fractions.length) {
      clamped[fractions[i++].b]++;
      allocated++;
    }
    return clamped;
  }

  const targets = emptyBucketRecord();
  const totalWeight = RARITY_BUCKETS.reduce((s, b) => s + (weights[b] ?? 0), 0);
  if (totalWeight > 0 && bundleSize > 0) {
    const raw = emptyBucketRecord();
    for (const b of RARITY_BUCKETS) {
      raw[b] = ((weights[b] ?? 0) / totalWeight) * bundleSize;
      targets[b] = Math.floor(raw[b]);
    }
    let allocated = RARITY_BUCKETS.reduce((s, b) => s + targets[b], 0);
    const remainders = RARITY_BUCKETS.map((b) => ({
      b,
      frac: raw[b] - targets[b],
    })).sort((a, x) => x.frac - a.frac);
    let i = 0;
    while (allocated < bundleSize && i < remainders.length) {
      targets[remainders[i++].b]++;
      allocated++;
    }
  }

  // Bump buckets up to their min guarantees, decrementing the lowest-priority
  // buckets first so the total still sums to bundleSize.
  const orderAsc = [...RARITY_BUCKETS].sort(
    (a, x) => BUCKET_PRIORITY[a] - BUCKET_PRIORITY[x],
  );
  for (const b of RARITY_BUCKETS) {
    const min = minGuarantees[b] ?? 0;
    if (targets[b] >= min) continue;
    let need = min - targets[b];
    targets[b] = min;
    // First pass: only steal surplus above each bucket's own min guarantee.
    for (const other of orderAsc) {
      if (other === b || need === 0) continue;
      const surplus = targets[other] - (minGuarantees[other] ?? 0);
      if (surplus <= 0) continue;
      const take = Math.min(surplus, need);
      targets[other] -= take;
      need -= take;
    }
    // Fallback: still short — steal from lowest-priority buckets even if it
    // dips below their min. Should only happen when min sum ≈ bundleSize.
    if (need > 0) {
      for (const other of orderAsc) {
        if (other === b || need === 0) continue;
        const take = Math.min(targets[other], need);
        targets[other] -= take;
        need -= take;
      }
    }
  }

  return targets;
}

export interface PickerInput {
  profile: WeightProfile;
  profileName: WeightProfileName;
  unlistedCards: Card[];
}

/**
 * Weighted bundle picker. Rows are atomic (added with full quantity),
 * duplicates are tracked by `card.id` and the cap counts copies (not rows).
 */
export function pickWeightedBundle(input: PickerInput): WeightedPickResult {
  const { profile, profileName } = input;

  const filtered = applyProfileFilter(
    profileName,
    input.unlistedCards.filter((c) => c.quantity > 0),
  );
  const eligible = filtered.filter(
    (c) => c.quantity <= profile.maxDuplicatesPerCard,
  );

  const targets = getTargetCounts(profile);

  const buckets: Record<RarityBucket, Card[]> = {
    common: [],
    uncommon: [],
    rare: [],
    holo: [],
    ultraRare: [],
  };
  for (const c of eligible) {
    buckets[normalizeRarityBucket(c.rarity, c.finish)].push(c);
  }
  // Stable, deterministic non-price tie-breaker: order by id within each bucket.
  // Selection must remain rarity-driven only — no value steering.
  for (const b of RARITY_BUCKETS) {
    buckets[b].sort((a, x) => (a.id < x.id ? -1 : a.id > x.id ? 1 : 0));
  }

  const picked: Card[] = [];
  const usedIds = new Set<string>();
  const filled = emptyBucketRecord();
  const dupCount = new Map<string, number>();
  const reasons: Record<string, PickReason> = {};

  // Pass 1: fill each bucket up to its target without overshooting. Cards
  // pulled in while `filled[b] < min` count toward the bucket's
  // min-guarantee floor; later picks within the same bucket are weight-driven.
  for (const b of RARITY_BUCKETS) {
    const target = targets[b];
    const min = Math.min(profile.minGuarantees[b] ?? 0, target);
    for (const c of buckets[b]) {
      if (filled[b] >= target) break;
      if (usedIds.has(c.id)) continue;
      const used = dupCount.get(c.id) ?? 0;
      if (used + c.quantity > profile.maxDuplicatesPerCard) continue;
      if (filled[b] + c.quantity > target) continue;
      const isMin = filled[b] < min;
      picked.push(c);
      usedIds.add(c.id);
      filled[b] += c.quantity;
      dupCount.set(c.id, used + c.quantity);
      reasons[c.id] = {
        bucket: b,
        kind: isMin ? "minGuarantee" : "weight",
        label: isMin
          ? `Meets ${BUCKET_LABEL[b]} minimum`
          : `Picked for ${BUCKET_LABEL[b]} bucket`,
      };
    }
  }

  // Pass 2: backfill any unfilled bundle capacity from leftover eligible rows.
  let totalFilled = RARITY_BUCKETS.reduce((s, b) => s + filled[b], 0);
  if (totalFilled < profile.bundleSize) {
    for (const b of RARITY_BUCKETS) {
      for (const c of buckets[b]) {
        if (totalFilled >= profile.bundleSize) break;
        if (usedIds.has(c.id)) continue;
        const used = dupCount.get(c.id) ?? 0;
        if (used + c.quantity > profile.maxDuplicatesPerCard) continue;
        if (totalFilled + c.quantity > profile.bundleSize) continue;
        picked.push(c);
        usedIds.add(c.id);
        filled[b] += c.quantity;
        dupCount.set(c.id, used + c.quantity);
        totalFilled += c.quantity;
        reasons[c.id] = {
          bucket: b,
          kind: "backfill",
          label: `Backfill from ${BUCKET_LABEL[b]} bucket`,
        };
      }
      if (totalFilled >= profile.bundleSize) break;
    }
  }

  const bucketShortfalls: BucketShortfall[] = [];
  for (const b of RARITY_BUCKETS) {
    const min = profile.minGuarantees[b] ?? 0;
    if (min > 0 && filled[b] < min) {
      bucketShortfalls.push({
        bucket: b,
        target: min,
        filled: filled[b],
        message: `Couldn't meet ${BUCKET_LABEL[b]} minimum of ${min} — only ${filled[b]} available.`,
      });
    }
  }

  return {
    cards: picked,
    selectedQty: totalFilled,
    targetSize: profile.bundleSize,
    bucketTargets: targets,
    bucketFilled: filled,
    bucketShortfalls,
    totalShortfall: Math.max(0, profile.bundleSize - totalFilled),
    reasons,
  };
}

export interface BundleSummary {
  rarityCounts: Record<RarityBucket, number>;
  estimatedMarketValue: number;
  totalCards: number;
}

export function summarizeBundle(cards: Card[]): BundleSummary {
  const rarityCounts = emptyBucketRecord();
  let estimatedMarketValue = 0;
  let totalCards = 0;
  for (const c of cards) {
    const b = normalizeRarityBucket(c.rarity, c.finish);
    rarityCounts[b] += c.quantity;
    estimatedMarketValue += (c.marketPrice ?? 0) * c.quantity;
    totalCards += c.quantity;
  }
  return { rarityCounts, estimatedMarketValue, totalCards };
}

const BASE_PROFILES: Record<
  WeightProfileName,
  Omit<WeightProfile, "bundleSize">
> = {
  standard: {
    name: "standard",
    weights: { common: 50, uncommon: 25, rare: 15, holo: 8, ultraRare: 2 },
    minGuarantees: { rare: 2, holo: 1 },
    maxDuplicatesPerCard: 4,
  },
  premium: {
    name: "premium",
    weights: { common: 0, uncommon: 10, rare: 30, holo: 40, ultraRare: 20 },
    minGuarantees: { holo: 2, ultraRare: 1 },
    maxDuplicatesPerCard: 2,
  },
  bulk: {
    name: "bulk",
    weights: { common: 70, uncommon: 22, rare: 6, holo: 2, ultraRare: 0 },
    minGuarantees: { rare: 1 },
    maxDuplicatesPerCard: 8,
  },
  pokemonType: {
    name: "pokemonType",
    weights: { common: 45, uncommon: 25, rare: 20, holo: 8, ultraRare: 2 },
    minGuarantees: { rare: 1 },
    maxDuplicatesPerCard: 4,
  },
  trainer: {
    name: "trainer",
    weights: { common: 70, uncommon: 25, rare: 5, holo: 0, ultraRare: 0 },
    minGuarantees: {},
    maxDuplicatesPerCard: 4,
  },
  energy: {
    name: "energy",
    weights: { common: 100, uncommon: 0, rare: 0, holo: 0, ultraRare: 0 },
    minGuarantees: {},
    maxDuplicatesPerCard: 8,
  },
};

/**
 * Per-bucket overrides a seller can apply on top of a base profile to tune the
 * rarity mix (e.g. "give me 3 holos minimum in a 50-card lot"). Any bucket
 * left undefined falls back to the base profile's value.
 */
export interface MixOverride {
  weights?: Partial<Record<RarityBucket, number>>;
  minGuarantees?: Partial<Record<RarityBucket, number>>;
  maxDuplicatesPerCard?: number;
}

export function buildWeightedProfile(
  name: WeightProfileName,
  bundleSize: number,
  override?: MixOverride,
): WeightProfile {
  const base = BASE_PROFILES[name];
  if (!override) return { ...base, bundleSize };
  const weights: Record<RarityBucket, number> = { ...base.weights };
  const minGuarantees: Partial<Record<RarityBucket, number>> = {
    ...base.minGuarantees,
  };
  if (override.weights) {
    for (const b of RARITY_BUCKETS) {
      const v = override.weights[b];
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
        weights[b] = v;
      }
    }
  }
  if (override.minGuarantees) {
    for (const b of RARITY_BUCKETS) {
      const v = override.minGuarantees[b];
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
        minGuarantees[b] = Math.floor(v);
      }
    }
  }
  const maxDup =
    typeof override.maxDuplicatesPerCard === "number" &&
    Number.isFinite(override.maxDuplicatesPerCard) &&
    override.maxDuplicatesPerCard > 0
      ? Math.floor(override.maxDuplicatesPerCard)
      : base.maxDuplicatesPerCard;
  return {
    name: base.name,
    bundleSize,
    weights,
    minGuarantees,
    maxDuplicatesPerCard: maxDup,
  };
}

export const WEIGHT_PROFILES = BASE_PROFILES;
export const BUCKET_LABELS = BUCKET_LABEL;
